/**
 * Intro Script Prompts
 * 
 * Functions for generating podcast episode introduction scripts.
 * Uses canonical field definitions via context parameter.
 */

import aiService from '../../services/aiService.js';
import {
    COMMON_GUARDRAILS,
    buildStructuredPrompt
} from '../../utils/promptTemplates.js';

/**
 * Generate Intro Script
 * Creates a compelling introduction script for podcast episodes
 * 
 * @param {object} context - { guestName, guestBio, podcastName, tone }
 * @returns {Promise<string>} - The intro script
 */
export const generateIntroScript = async (context) => {
    const tone = context.tone || 'High Energy and Professional';

    const sections = {
        role: "You are a professional Voiceover Scriptwriter. Write a compelling intro script.",

        context: `
**SHOW:** ${context.podcastName || 'The Podcast'}
**GUEST:** ${context.guestName}
**BIO:** ${context.guestBio}
        `,

        task: `
Write a 30-60 second intro script that hooks the listener immediately.
The tone must be **${tone}**.
        `,

        instructions: `
- Start with a **Hook** (a bold statement or question).
- Introduce the guest with *authority*.
- Tease what the listener will learn properly.
- End with "Please welcome... ${context.guestName}."
- Use formatting (bold/italics) to indicate emphasis for the voice actor.
        `,

        format: `
**OUTPUT FORMAT:**
(Markdown text ready to read)
        `,

        guardrails: COMMON_GUARDRAILS
    };

    const prompt = buildStructuredPrompt(sections);
    return await aiService.generateContent(prompt, 'PLANNING');
};

const introScriptExports = {
    generateIntroScript
};

export default introScriptExports;
